import * as signalR from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import { IRealtimeTransport } from "./ITransport";
import { ConnectionState } from "../types/events";

/**
 * SignalR Transport Implementation
 * Integrates MessagePack binary protocol for high throughput, low latency, and low memory usage.
 */
export class SignalRTransport extends IRealtimeTransport {
    constructor() {
        super();
        this.connection = null;
        this.state = ConnectionState.DISCONNECTED;
        this.stateChangeListeners = new Set();
        this.pendingSubscriptions = new Map(); // Store subscriptions made before connection is built
    }

    /**
     * Build and start the SignalR Hub Connection
     * @param {Object} config - { url, accessTokenFactory, useMessagePack = true }
     */
    async connect(config = {}) {
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            return;
        }

        const rawBase = config.url || process.env.REACT_APP_SIGNALR_URL || process.env.REACT_APP_SITE_URL || "https://alarabeyya.com";
        const cleanBase = rawBase.replace(/\/+$/, "");
        const backendUrl = cleanBase.endsWith("/signalr") ? cleanBase : `${cleanBase}/signalr`;
        const useMessagePack = config.useMessagePack !== false;

        let builder = new signalR.HubConnectionBuilder()
            .withUrl(backendUrl, {
                skipNegotiation: false,
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
                ...(config.accessTokenFactory ? { accessTokenFactory: config.accessTokenFactory } : {})
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(process.env.NODE_ENV === "development" ? signalR.LogLevel.Information : signalR.LogLevel.None);

        if (useMessagePack) {
            try {
                builder = builder.withHubProtocol(new MessagePackHubProtocol());
            } catch (err) {
                console.warn("[SignalRTransport] MessagePack protocol initialization fallback to JSON:", err);
            }
        }

        this.connection = builder.build();
        this.setupLifecycleHandlers();

        // Restore any pending subscriptions
        for (const [eventName, handlers] of this.pendingSubscriptions.entries()) {
            handlers.forEach(handler => {
                this.connection.on(eventName, handler);
            });
        }

        this.setState(ConnectionState.CONNECTING);
        try {
            await this.connection.start();
            this.setState(ConnectionState.CONNECTED);
            console.log("[SignalRTransport] Connected successfully via MessagePack/WebSockets. ConnectionId:", this.connection.connectionId);
        } catch (error) {
            this.setState(ConnectionState.DISCONNECTED);
            console.error("[SignalRTransport] Connection failed:", error);
            // Non-blocking error handling
        }
    }

    setupLifecycleHandlers() {
        if (!this.connection) return;

        this.connection.onreconnecting((error) => {
            console.warn("[SignalRTransport] Reconnecting...", error);
            this.setState(ConnectionState.RECONNECTING);
        });

        this.connection.onreconnected((connectionId) => {
            console.log("[SignalRTransport] Reconnected successfully. ConnectionId:", connectionId);
            this.setState(ConnectionState.CONNECTED);
        });

        this.connection.onclose((error) => {
            console.warn("[SignalRTransport] Connection closed:", error);
            this.setState(ConnectionState.DISCONNECTED);
        });
    }

    async disconnect() {
        if (this.connection) {
            try {
                await this.connection.stop();
            } catch (err) {
                console.error("[SignalRTransport] Error stopping connection:", err);
            } finally {
                this.setState(ConnectionState.DISCONNECTED);
            }
        }
    }

    async invoke(methodName, ...args) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            console.warn(`[SignalRTransport] Attempted to invoke '${methodName}' while not connected.`);
            return null;
        }
        try {
            return await this.connection.invoke(methodName, ...args);
        } catch (error) {
            console.error(`[SignalRTransport] Error invoking '${methodName}':`, error);
            throw error;
        }
    }

    on(eventName, handler) {
        if (!this.pendingSubscriptions.has(eventName)) {
            this.pendingSubscriptions.set(eventName, new Set());
        }
        this.pendingSubscriptions.get(eventName).add(handler);

        if (this.connection) {
            this.connection.on(eventName, handler);
        }
    }

    off(eventName, handler) {
        if (this.pendingSubscriptions.has(eventName)) {
            const handlers = this.pendingSubscriptions.get(eventName);
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.pendingSubscriptions.delete(eventName);
            }
        }

        if (this.connection) {
            if (handler) {
                this.connection.off(eventName, handler);
            } else {
                this.connection.off(eventName);
            }
        }
    }

    getState() {
        return this.state;
    }

    getConnectionId() {
        return this.connection ? this.connection.connectionId : null;
    }

    setState(newState) {
        this.state = newState;
        this.stateChangeListeners.forEach(listener => {
            try {
                listener(newState);
            } catch (e) {
                console.error("[SignalRTransport] Error in stateChangeListener:", e);
            }
        });
    }

    onStateChange(listener) {
        this.stateChangeListeners.add(listener);
        return () => this.stateChangeListeners.delete(listener);
    }
}
